import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import {
  useApi,
  ApiError,
  type ProfileAlert,
  type WhanauProfileResponse,
} from "../api/client";
import { AssessmentWizard } from "./AssessmentWizard";
import { deriveSubmissionState } from "./useSubmissionState";
import { CycleHistoryStrip } from "./CycleHistoryStrip";
import { WhanauShareView } from "./WhanauShareView";
import { WhanauSurveyMobile } from "./WhanauSurveyMobile";
import { SharePinModal } from "./SharePinModal";
import {
  ProfileTiles,
  ProfileTileModal,
  ProfileHeaderTile,
  CaseworkersList,
  ServicesList,
  FactorsReadOnlyList,
  type TileKey,
} from "./ProfileTiles";
import type { SharePinStatusResponse } from "../api/client";
import { RecommendationsPanel } from "./RecommendationsPanel";
import { useFramework } from "../framework";
import {
  deriveRecommendations,
  statusByKey,
  summarise,
} from "../recommendations/derive";
import "./WhanauProfile.css";
import { formatDate } from "../api/formatDate";

/**
 * Per-whānau profile view at /whanau/:whanauId.
 *
 * Editor flow (Phase 1):
 *   • Each subdomain has a StatePicker; selecting a state marks it pending.
 *   • If the chosen state is 'not-assessed', a NotAssessedReasonPicker
 *     appears beneath it (and the row stays incomplete until a reason is
 *     picked, which disables the Save button until resolved).
 *   • A Save bar appears once any cell is dirty. Save POSTs the batch to
 *     /api/v1/whanau/:id/assessments, refetches the profile, and clears
 *     the pending map.
 *   • Cancel discards pending changes.
 *
 * Auth is enforced server-side. Manager+ overrides land in the audit log
 * (see whanauById.ts and whanauAssessments.ts).
 */

export function WhanauProfile() {
  const { whanauId } = useParams<{ whanauId: string }>();
  const { t } = useTranslation();
  const callApi = useApi();
  const { findSubdomain } = useFramework();

  const [state, setState] = useState<
    | { kind: "loading" }
    | { kind: "ok"; data: WhanauProfileResponse }
    | { kind: "notFound" }
    | { kind: "error"; message: string; status?: number }
  >({ kind: "loading" });

  const [wizardOpen, setWizardOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  /** Which profile tile is currently expanded into a modal. null = none. */
  const [openTile, setOpenTile] = useState<TileKey>(null);
  // PIN-gated entry to share mode. 'check' = looking up PIN status,
  // 'set' = no PIN, prompt for setup, then opens share. 'idle' = closed.
  const [pinFlow, setPinFlow] = useState<
    { kind: "idle" } | { kind: "check" } | { kind: "set" } | { kind: "verify" }
  >({ kind: "idle" });

  // Fetch on mount / param change.
  const loadProfile = useCallback(async () => {
    if (!whanauId) {
      setState({ kind: "error", message: "No whānau id in URL" });
      return;
    }
    setState({ kind: "loading" });
    try {
      const data = await callApi<WhanauProfileResponse>(`/whanau/${whanauId}`);
      setState({ kind: "ok", data });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 404) {
          setState({ kind: "notFound" });
        } else {
          setState({ kind: "error", status: err.status, message: err.message });
        }
      } else {
        const message = err instanceof Error ? err.message : "Unknown error";
        setState({ kind: "error", message });
      }
    }
    // callApi is a fresh closure each render; deliberately excluded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whanauId]);

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  // ---- Share-mode entry ---------------------------------------------------
  // Flow:
  //   1. Click "Share with whānau" → setPinFlow('check')
  //   2. Fetch PIN status. If no PIN → setPinFlow('set'). If PIN → open share.
  //   3. On 'set' modal success → open share.
  //   4. While share open: any dismiss attempt → setPinFlow('verify').
  //   5. On 'verify' success → close share modal.
  const onShareClick = useCallback(async () => {
    setPinFlow({ kind: "check" });
    try {
      const status = await callApi<SharePinStatusResponse>("/me/share-pin");
      if (status.has_pin) {
        setPinFlow({ kind: "idle" });
        setShareOpen(true);
      } else {
        setPinFlow({ kind: "set" });
      }
    } catch {
      // On error, still open share — fail open is friendlier than blocking
      // entirely. If the API is down, the share view itself will surface
      // the issue.
      setPinFlow({ kind: "idle" });
      setShareOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Dirty-state navigation guards were removed when Factors became
  // read-only (#98). The wizard manages its own dirty-state confirm
  // internally. If we re-introduce inline editing, restore useBlocker +
  // beforeunload from git history.

  return (
    <article className="whanau-profile" aria-labelledby="profile-title">
      {state.kind === "loading" && (
        <p className="whanau-profile__status">{t("profile.loading")}</p>
      )}

      {state.kind === "notFound" && (
        <p className="whanau-profile__status whanau-profile__status--error">
          {t("profile.notFound")}
        </p>
      )}

      {state.kind === "error" && (
        <p className="whanau-profile__status whanau-profile__status--error">
          {state.status
            ? `${String(state.status)} — ${state.message}`
            : state.message}
        </p>
      )}

      {state.kind === "ok" &&
        (() => {
          // Submission rules derived once, used by ProfileHeaderTile to
          // disable the Start-an-assessment button when blocked.
          const submission = deriveSubmissionState(state.data.sessions);
          // Recommendations derived from the latest assessment (ADR-009).
          // Done once here so the tile count and the panel agree.
          const { recommendations, strengths } = deriveRecommendations(
            state.data.assessments,
            state.data.alerts,
            (id) => findSubdomain(id)?.toCOutcome,
          );
          const recStatuses = statusByKey(state.data.recommendations);
          const recSummary = summarise(recommendations, recStatuses);
          return (
            <>
              <ProfileHeaderTile
                name={state.data.whanau.display_name}
                metaRows={[
                  {
                    label: t("profile.engagement"),
                    value: (
                      <span
                        className={`whanau-profile__engagement whanau-profile__engagement--${state.data.whanau.engagement_state}`}
                      >
                        {state.data.whanau.engagement_state}
                      </span>
                    ),
                  },
                  ...(state.data.whanau.primary_location
                    ? [
                        {
                          label: t("profile.location"),
                          value: state.data.whanau.primary_location,
                        },
                      ]
                    : []),
                  ...(state.data.whanau.primary_iwi
                    ? [
                        {
                          label: t("profile.iwi"),
                          value: state.data.whanau.primary_iwi,
                        },
                      ]
                    : []),
                  {
                    label: t("profile.registered"),
                    value: formatDate(state.data.whanau.registered_at),
                  },
                ]}
                onStartAssessment={() => setWizardOpen(true)}
                startAssessmentDisabled={!submission.canSubmit}
                startAssessmentDisabledHint={
                  submission.blockingSignoff?.status === "pending"
                    ? t("profile.submitBlockedPendingHint")
                    : t("profile.submitBlockedReturnedHint")
                }
                onShare={() => void onShareClick()}
                shareDisabled={pinFlow.kind === "check"}
                shareLabel={
                  pinFlow.kind === "check"
                    ? t("profile.shareChecking")
                    : `${t("profile.shareWithWhanau")} →`
                }
                startAssessmentLabel={`${t("profile.startAssessment")} →`}
              />

              {submission.blockingSignoff && (
                <div
                  className={`profile-banner profile-banner--${submission.blockingSignoff.status}`}
                  role="status"
                >
                  {submission.blockingSignoff.status === "pending"
                    ? t("profile.submitBlockedPending", {
                        cycle: t(
                          `wizard.cycle.${submission.blockingSignoff.cycleLabel}`,
                          {
                            defaultValue: submission.blockingSignoff.cycleLabel,
                          },
                        ),
                      })
                    : t("profile.submitBlockedReturned", {
                        cycle: t(
                          `wizard.cycle.${submission.blockingSignoff.cycleLabel}`,
                          {
                            defaultValue: submission.blockingSignoff.cycleLabel,
                          },
                        ),
                      })}
                </div>
              )}

              <ProfileTiles
                members={state.data.members}
                services={state.data.services ?? []}
                keyworkers={state.data.keyworkers}
                sessions={state.data.sessions}
                assessments={state.data.assessments}
                alerts={state.data.alerts}
                recommendationOpenCount={recSummary.openCount}
                recommendationUrgentCount={recSummary.urgentOpenCount}
                onTileClick={(which) => setOpenTile(which)}
              />

              {openTile === "members" && (
                <ProfileTileModal
                  title={t("profileTiles.members.title")}
                  onClose={() => setOpenTile(null)}
                >
                  <MemberList members={state.data.members} />
                </ProfileTileModal>
              )}

              {openTile === "services" && (
                <ProfileTileModal
                  title={t("profileTiles.services.title")}
                  onClose={() => setOpenTile(null)}
                >
                  <ServicesList services={state.data.services ?? []} />
                </ProfileTileModal>
              )}

              {openTile === "caseworkers" && (
                <ProfileTileModal
                  title={t("profileTiles.caseworkers.title")}
                  onClose={() => setOpenTile(null)}
                >
                  <CaseworkersList keyworkers={state.data.keyworkers} />
                  <WhanauSurveyMobile whanauId={state.data.whanau.whanau_id} />
                </ProfileTileModal>
              )}

              {openTile === "assessments" && (
                <ProfileTileModal
                  title={t("profileTiles.assessments.title")}
                  onClose={() => setOpenTile(null)}
                >
                  <CycleHistoryStrip
                    whanauId={state.data.whanau.whanau_id}
                    sessions={state.data.sessions}
                  />
                </ProfileTileModal>
              )}

              {openTile === "factors" && (
                <ProfileTileModal
                  title={t("profileTiles.factors.title")}
                  onClose={() => setOpenTile(null)}
                >
                  {state.data.alerts.length > 0 && (
                    <AlertsBanner alerts={state.data.alerts} />
                  )}
                  <p className="profile-factors-hint">
                    {t("profile.factorsReadOnlyHint")}
                  </p>
                  {/*
                Read-only grouped list: domain → subdomain → current state
                + when recorded + change-since-last. State changes always
                go through the assessment session wizard.
              */}
                  <FactorsReadOnlyList assessments={state.data.assessments} />
                </ProfileTileModal>
              )}

              {openTile === "recommendations" && (
                <ProfileTileModal
                  title={t("profileTiles.recommendations.title")}
                  onClose={() => setOpenTile(null)}
                >
                  <RecommendationsPanel
                    whanauId={state.data.whanau.whanau_id}
                    recommendations={recommendations}
                    strengths={strengths}
                    statuses={state.data.recommendations}
                    onActed={() => void loadProfile()}
                  />
                </ProfileTileModal>
              )}

              {wizardOpen && (
                <AssessmentWizard
                  whanauId={state.data.whanau.whanau_id}
                  profile={state.data}
                  onClose={() => setWizardOpen(false)}
                  onSaved={() => void loadProfile()}
                />
              )}

              {shareOpen && (
                <WhanauShareView
                  whanauId={state.data.whanau.whanau_id}
                  profile={state.data}
                  // Dismiss attempts in share mode never close directly; they
                  // open the PIN verify modal. The verify success handler is
                  // what actually closes share mode.
                  onClose={() => setPinFlow({ kind: "verify" })}
                />
              )}

              {pinFlow.kind === "set" && (
                <SharePinModal
                  mode="set"
                  onSuccess={() => {
                    setPinFlow({ kind: "idle" });
                    setShareOpen(true);
                  }}
                  onCancel={() => setPinFlow({ kind: "idle" })}
                />
              )}

              {pinFlow.kind === "verify" && (
                <SharePinModal
                  mode="verify"
                  onSuccess={() => {
                    // Audit share_mode.exited before closing. Best-effort.
                    void callApi("/me/share-mode/event", {
                      method: "POST",
                      body: JSON.stringify({
                        whanau_id: state.data.whanau.whanau_id,
                        event: "exited",
                      }),
                      headers: { "content-type": "application/json" },
                    }).catch(() => {
                      // intentionally swallowed; audit failure doesn't block exit.
                    });
                    setPinFlow({ kind: "idle" });
                    setShareOpen(false);
                  }}
                  onCancel={() => setPinFlow({ kind: "idle" })}
                />
              )}
            </>
          );
        })()}
    </article>
  );
}

// ---------------------------------------------------------------------------
// Alerts banner — firing framework_alert_definition rules for this whānau.
// ---------------------------------------------------------------------------
function AlertsBanner({ alerts }: { alerts: ProfileAlert[] }) {
  const { t } = useTranslation();
  // Sort by severity (urgent → warn → info) then by sort_order.
  const severityRank: Record<ProfileAlert["severity"], number> = {
    urgent: 1,
    warn: 2,
    info: 3,
  };
  const sorted = [...alerts].sort((a, b) => {
    const r = severityRank[a.severity] - severityRank[b.severity];
    if (r !== 0) return r;
    return a.sort_order - b.sort_order;
  });

  const topSeverity = sorted[0].severity;

  return (
    <section
      className={`alerts-banner alerts-banner--${topSeverity}`}
      aria-label={t("profile.alerts.heading")}
    >
      <h3 className="alerts-banner__heading">
        {t("profile.alerts.heading")}{" "}
        <span className="alerts-banner__count">
          {t("profile.alerts.count", { count: sorted.length })}
        </span>
      </h3>
      <ul className="alerts-banner__list">
        {sorted.map((a) => (
          <li
            key={a.alert_id}
            className={`alerts-banner__item alerts-banner__item--${a.severity}`}
          >
            <span
              className="alerts-banner__icon"
              aria-hidden="true"
              data-severity={a.severity}
            >
              ●
            </span>
            <div className="alerts-banner__body">
              <p className="alerts-banner__name">
                {t(`profile.alerts.label.${a.alert_id}`, {
                  defaultValue: a.name_en,
                })}
              </p>
              {a.description && (
                <p className="alerts-banner__desc">{a.description}</p>
              )}
              <p className="alerts-banner__source">
                {t("profile.alerts.source", {
                  subdomain: a.subdomain_name_en,
                })}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

// Legacy ProfileHeader removed — replaced by ProfileHeaderTile in ProfileTiles.tsx.

// ---------------------------------------------------------------------------
// Members — expandable rows showing full member detail on click.
// ---------------------------------------------------------------------------
function MemberList({
  members,
}: {
  members: WhanauProfileResponse["members"];
}) {
  const { t } = useTranslation();
  const [expandedMember, setExpandedMember] = useState<number | null>(null);

  return (
    <section className="whanau-profile__section">
      <h3 className="whanau-profile__section-title">
        {t("profile.members")}{" "}
        <span className="whanau-profile__section-count">
          {t("profile.memberCount", { count: members.length })}
        </span>
      </h3>
      {members.length === 0 ? (
        <p className="whanau-profile__status">{t("profile.noMembers")}</p>
      ) : (
        <ul className="member-list">
          {members.map((m) => {
            const isExpanded = expandedMember === m.whanau_member_id;
            return (
              <li
                key={m.whanau_member_id}
                className={
                  m.is_primary_contact
                    ? "member-row member-row--primary"
                    : "member-row"
                }
              >
                <button
                  type="button"
                  className="member-row__head"
                  aria-expanded={isExpanded}
                  aria-controls={`member-detail-${String(m.whanau_member_id)}`}
                  onClick={() =>
                    setExpandedMember((cur) =>
                      cur === m.whanau_member_id ? null : m.whanau_member_id,
                    )
                  }
                >
                  <div className="member-row__main">
                    <p className="member-row__name">
                      {m.display_name}
                      {m.is_primary_contact && (
                        <span className="member-row__primary-badge">
                          {t("profile.primaryContact")}
                        </span>
                      )}
                    </p>
                    <p className="member-row__meta">
                      {[m.relationship, m.age_band, m.gender]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  </div>
                  <span className="member-row__expand" aria-hidden="true">
                    {isExpanded ? "▼" : "▶"}
                  </span>
                </button>

                {isExpanded && (
                  <div
                    id={`member-detail-${String(m.whanau_member_id)}`}
                    className="member-row__detail"
                  >
                    <dl>
                      {m.relationship && (
                        <>
                          <dt>{t("profile.member.relationship")}</dt>
                          <dd>{m.relationship}</dd>
                        </>
                      )}
                      {m.age_band && (
                        <>
                          <dt>{t("profile.member.ageBand")}</dt>
                          <dd>{m.age_band}</dd>
                        </>
                      )}
                      {m.gender && (
                        <>
                          <dt>{t("profile.member.gender")}</dt>
                          <dd>{m.gender}</dd>
                        </>
                      )}
                      <dt>{t("profile.member.recordedAt")}</dt>
                      <dd>{formatDate(m.recorded_at)}</dd>
                      {m.exess_person_id !== null && (
                        <>
                          <dt>{t("profile.member.exessPersonId")}</dt>
                          <dd>
                            <code className="member-row__exess-id">
                              {m.exess_person_id}
                            </code>
                          </dd>
                        </>
                      )}
                      <dt>{t("profile.member.notes")}</dt>
                      <dd>
                        {m.member_notes ? (
                          <p className="member-row__notes">{m.member_notes}</p>
                        ) : (
                          <span className="member-row__notes-empty">
                            {t("profile.member.noNotes")}
                          </span>
                        )}
                      </dd>
                    </dl>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
